import Microgame from '../engine/Microgame.js';

export default class SpreadGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Étale !`, en: `Spread!`, es: `¡Unta!` },
            controls: { fr: `Glisse ton doigt d'un point à l'autre de la tartine`, en: `Swipe your finger across the bread`, es: `Desliza tu dedo por el pan` },
            objective: { fr: `Étale uniformément la garniture partout`, en: `Spread the topping evenly everywhere`, es: `Esparce la cobertura de manera uniforme por todas partes` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><rect x="3" y="10" width="18" height="10" rx="3"/><path d="M7 6c0-1.5 2-3 5-3s5 1.5 5 3v4H7V6Z"/><path d="m2 14 20-2"/></svg>`,
            actions: [
                { type: 'wait', duration: 220 },
                { type: 'swipe', fromX: 36, fromY: 26, toX: 66, toY: 32, duration: 420 },
                { type: 'swipe', fromX: 34, fromY: 36, toX: 68, toY: 42, duration: 420 },
                { type: 'swipe', fromX: 36, fromY: 46, toX: 66, toY: 52, duration: 420 },
                { type: 'swipe', fromX: 34, fromY: 56, toX: 68, toY: 62, duration: 420 },
                { type: 'wait', duration: 400 },
            ],
            loopDuration: 3600
        };
    }
    constructor(engine) {
        super(engine);
        this.knife = null;
        this.coverage = 0;
        this.isDragging = false;
        
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeedScale = 1 + (speedMult - 1) * 0.4;
        this.duration = 3500 * (speedMult / adjustedSpeedScale);
    }

    setup() {
        this.engine.ui.instruction.textContent = "SPRAY!";

        // Wall Background
        this.wall = document.createElement('div');
        this.wall.style.position = 'absolute';
        this.wall.style.width = '240px';
        this.wall.style.height = '240px';
        this.wall.style.top = '50%';
        this.wall.style.left = '50%';
        this.wall.style.transform = 'translate(-50%, -50%)';
        this.wall.style.background = '#2a2a2a'; // Dark wall
        this.wall.style.border = `4px solid ${this.secondaryColor}`;
        this.wall.style.borderRadius = '10px';
        this.wall.style.overflow = 'hidden';
        this.wall.style.boxShadow = `0 0 20px rgba(0,0,0,0.5)`;
        
        // Brick pattern simple approximation
        this.wall.style.backgroundImage = `repeating-linear-gradient(0deg, transparent, transparent 18px, rgba(0,0,0,0.3) 18px, rgba(0,0,0,0.3) 20px), repeating-linear-gradient(90deg, transparent, transparent 38px, rgba(0,0,0,0.3) 38px, rgba(0,0,0,0.3) 40px)`;
        
        this.container.appendChild(this.wall);

        // SVG Tag (70% finished)
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("viewBox", "0 0 100 100");
        svg.style.width = '100%';
        svg.style.height = '100%';
        svg.style.position = 'absolute';
        svg.style.zIndex = '5';
        
        // Randomization Logic
        this.missingPart = this.isPlayback ? 'eyes' : (Math.random() > 0.5 ? 'mouth' : 'eyes');
        const eyeTypes = ['dot', 'cross', 'sleepy'];
        const selectedEyeType = this.isPlayback ? 'dot' : eyeTypes[Math.floor(Math.random() * eyeTypes.length)];
        const mouthTypes = ['smile', 'sad', 'neutral'];
        const selectedMouthType = this.isPlayback ? 'smile' : mouthTypes[Math.floor(Math.random() * mouthTypes.length)];

        // Eyes
        const eyesG = document.createElementNS("http://www.w3.org/2000/svg", "g");
        eyesG.setAttribute("stroke", this.primaryColor);
        eyesG.setAttribute("fill", selectedEyeType === 'dot' ? this.primaryColor : 'none');
        eyesG.setAttribute("stroke-width", "8");
        eyesG.setAttribute("stroke-linecap", "round");
        eyesG.style.opacity = this.missingPart === 'eyes' ? '0.2' : '1';

        if (selectedEyeType === 'dot') {
            const eye1 = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            eye1.setAttribute("cx", "30"); eye1.setAttribute("cy", "35"); eye1.setAttribute("r", "8");
            const eye2 = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            eye2.setAttribute("cx", "70"); eye2.setAttribute("cy", "35"); eye2.setAttribute("r", "8");
            eyesG.appendChild(eye1);
            eyesG.appendChild(eye2);
        } else if (selectedEyeType === 'cross') {
            const cross1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
            cross1.setAttribute("d", "M 22 27 L 38 43 M 38 27 L 22 43");
            const cross2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
            cross2.setAttribute("d", "M 62 27 L 78 43 M 78 27 L 62 43");
            eyesG.appendChild(cross1);
            eyesG.appendChild(cross2);
        } else if (selectedEyeType === 'sleepy') {
            const sleepy1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
            sleepy1.setAttribute("d", "M 20 35 Q 30 25 40 35");
            const sleepy2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
            sleepy2.setAttribute("d", "M 60 35 Q 70 25 80 35");
            eyesG.appendChild(sleepy1);
            eyesG.appendChild(sleepy2);
        }

        // Mouth
        const mouth = document.createElementNS("http://www.w3.org/2000/svg", "path");
        mouth.setAttribute("fill", "none");
        mouth.setAttribute("stroke", this.primaryColor);
        mouth.setAttribute("stroke-width", "8");
        mouth.setAttribute("stroke-linecap", "round");
        mouth.style.opacity = this.missingPart === 'mouth' ? '0.2' : '1';

        if (selectedMouthType === 'smile') {
            mouth.setAttribute("d", "M 20 60 Q 50 90 80 60");
        } else if (selectedMouthType === 'sad') {
            mouth.setAttribute("d", "M 20 75 Q 50 45 80 75");
        } else if (selectedMouthType === 'neutral') {
            mouth.setAttribute("d", "M 25 70 L 75 70");
        }

        svg.appendChild(eyesG);
        svg.appendChild(mouth);

        // Tag drips (Random decor)
        for (let i = 0; i < 3; i++) {
            const dx = 20 + Math.random() * 60;
            const dy = 30 + Math.random() * 60;
            const drip = document.createElementNS("http://www.w3.org/2000/svg", "path");
            drip.setAttribute("d", `M ${dx} ${dy} L ${dx} ${dy + 15 + Math.random() * 20}`);
            drip.setAttribute("stroke", this.primaryColor);
            drip.setAttribute("stroke-width", "2");
            drip.setAttribute("stroke-linecap", "round");
            drip.setAttribute("opacity", "0.4");
            svg.appendChild(drip);
        }

        this.wall.appendChild(svg);

        // Paint layer (using CANVAS for max performance!)
        this.spreads = document.createElement('canvas');
        this.spreads.width = 240;
        this.spreads.height = 240;
        this.spreads.style.position = 'absolute';
        this.spreads.style.top = '0';
        this.spreads.style.left = '0';
        this.spreads.style.zIndex = '6'; 
        this.wall.appendChild(this.spreads);
        this.ctx = this.spreads.getContext('2d');

        // Target zone based on missing part
        if (this.missingPart === 'mouth') {
            this.targetRect = { x: 40, y: 120, w: 160, h: 100 };
        } else {
            this.targetRect = { x: 30, y: 30, w: 180, h: 90 };
        }

        // Spray Can Pointer
        this.knife = document.createElement('div');
        this.knife.style.position = 'absolute';
        this.knife.style.width = '40px';
        this.knife.style.height = '80px';
        this.knife.style.background = `linear-gradient(90deg, #ccc, #fff, #999)`;
        this.knife.style.borderRadius = '10px 10px 5px 5px';
        this.knife.style.top = '50%';
        this.knife.style.left = '50%';
        this.knife.style.transform = 'translate(-7px, 0)'; // nozzle mapping directly to pointer
        this.knife.style.cursor = 'none'; 
        this.knife.style.zIndex = '15';
        this.knife.style.boxShadow = '0 5px 15px rgba(0,0,0,0.5)';
        
        // Spray Can details
        const cap = document.createElement('div');
        cap.style.position = 'absolute';
        cap.style.width = '20px';
        cap.style.height = '15px';
        cap.style.background = this.primaryColor;
        cap.style.top = '-15px';
        cap.style.left = '10px';
        cap.style.borderRadius = '5px 5px 0 0';
        this.knife.appendChild(cap);
        
        const nozzle = document.createElement('div');
        nozzle.style.position = 'absolute';
        nozzle.style.width = '6px';
        nozzle.style.height = '6px';
        nozzle.style.background = '#333';
        nozzle.style.top = '4px';
        nozzle.style.left = '-3px';
        cap.appendChild(nozzle);

        // Label
        const label = document.createElement('div');
        label.style.position = 'absolute';
        label.style.width = '40px';
        label.style.height = '40px';
        label.style.background = this.secondaryColor;
        label.style.top = '20px';
        this.knife.appendChild(label);

        this.container.appendChild(this.knife);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (this.won) return;
        this.isDragging = true;
        this.updateKnife(e);
        this.triggerSpray(e);
    }

    onMove(e) {
        if (this.won) return;
        this.updateKnife(e);

        if (this.isDragging) {
            this.triggerSpray(e);
        }
    }
    
    triggerSpray(e) {
        const wallRect = this.wall.getBoundingClientRect();
        
        if (e.clientX > wallRect.left && e.clientX < wallRect.right &&
            e.clientY > wallRect.top && e.clientY < wallRect.bottom) {
            
            const relX = e.clientX - wallRect.left;
            const relY = e.clientY - wallRect.top;

            // Ultra-performant canvas spray painting
            this.ctx.globalAlpha = 0.15;
            this.ctx.fillStyle = this.primaryColor;
            this.ctx.beginPath();
            this.ctx.arc(relX, relY, 20, 0, Math.PI * 2);
            this.ctx.fill();
            
            this.ctx.globalAlpha = 0.4;
            this.ctx.beginPath();
            this.ctx.arc(relX, relY, 10, 0, Math.PI * 2);
            this.ctx.fill();

            // Check if inside target zone
            if (relX > this.targetRect.x && relX < this.targetRect.x + this.targetRect.w &&
                relY > this.targetRect.y && relY < this.targetRect.y + this.targetRect.h) {
                
                // Throttled coverage increment based on distance moved
                const dx = relX - (this.lastTrackX || 0);
                const dy = relY - (this.lastTrackY || 0);
                
                if ((dx*dx + dy*dy) > 64) { // Must move at least 8px to count
                    this.lastTrackX = relX;
                    this.lastTrackY = relY;
                    this.coverage++;
                    
                    if (this.coverage > 22) {  // Requires an almost complete tracing
                        this.win();
                    }
                }
            }
        }
    }

    updateKnife(e) {
        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        this.knife.style.left = `${x}px`;
        this.knife.style.top = `${y}px`;
    }

    onUp() {
        this.isDragging = false;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
